import * as React from 'react';
import { Button } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../store';
import { combinedActionCreators } from '../../store/Combined';
import { trainingActionCreators } from '../../store/Training';
import { ChoiceValue, InterpretValue, CardOption } from '../../types/TrainingTypes';
import ChoiceOptions from './ChoiceOptions';

export default function Combined() {
    const trainingState = useAppSelector(state => state.training);
    const state = useAppSelector(state => state.combined);
    const dispatch = useAppDispatch();
    const [isTimerActive, setIsTimerActive] = React.useState(false);

    let borderColor = undefined;
    const reviews = trainingState.card?.resultInfo?.reviews;
    if (reviews && reviews >= 700)
        borderColor = '#001dda';
    else if (reviews && reviews >= 600)
        borderColor = '#4400da';
    else if (reviews && reviews >= 500)
        borderColor = '#9e00da';
    else if (reviews && reviews >= 400)
        borderColor = '#da0092';
    else if (reviews && reviews >= 300)
        borderColor = '#da0000';
    else if (reviews && reviews >= 200)
        borderColor = '#da6400'
    else if (reviews && reviews >= 100)
        borderColor = '#c5a900';

    function showOptions(isTimerElapsed: boolean) {
        dispatch(combinedActionCreators.showOptions(isTimerElapsed));
    }

    function nextCard() {
        dispatch(trainingActionCreators.nextCard());
    }

    function chooseOption(option: CardOption | undefined, isLong: boolean) {
        if (!isTimerActive) {
            const isCorrect = option?.content === state.solution;
            if (trainingState.isSolutionShown) {
                if (isCorrect || !trainingState.isServicePanelEnabled)
                    nextCard();
            }
            else {
                if (isCorrect && !trainingState.isServicePanelEnabled) {
                    setIsTimerActive(true);
                    setTimeout(() => {
                        setIsTimerActive(false);
                        nextCard();
                    }, 500);
                }
                dispatch(combinedActionCreators.chooseOption(option));

                if (!state.isTimerElapsed || !option) {
                    const assessmentValue = !option
                        ? isLong
                            ? InterpretValue.Reset
                            : InterpretValue.DoNotKnow
                        : isCorrect
                            ? isLong
                                ? InterpretValue.KnowWell
                                : InterpretValue.Know
                            : isLong
                                ? InterpretValue.MinimalDecrease
                                : InterpretValue.DoNotKnow;
                    dispatch(trainingActionCreators.interpretAssess(assessmentValue));
                }
                else {
                    const assessmentValue = isCorrect
                        ? isLong
                            ? ChoiceValue.Know
                            : ChoiceValue.Correct
                        : isLong
                            ? ChoiceValue.Decrease
                            : ChoiceValue.Incorrect;
                    const time = new Date().getTime() - new Date(state.shownTime!).getTime();

                    let difficulty = 0;
                    let passedCorrect = false;
                    for (let i = 0; i < state.options.length; i++) {
                        difficulty += Math.min(6 + state.options[i].content.length, passedCorrect ? 9 : 15);
                        passedCorrect ||= (state.options[i].content === option?.content);
                    }
                    dispatch(trainingActionCreators.choiceAssess(assessmentValue, time, difficulty));
                }
            }
        }
    }

    function resetTimer() {
        dispatch(combinedActionCreators.resetTimer());
    }

    function showExample() {
        dispatch(combinedActionCreators.showExample());
    }

    return (
        <>
            <div className="flex-grow-1 w-100 width-limit text-break">
                <div style={{ minHeight: '6.5rem' }}>
                    <div className="training-box mb-2"
                        style={{ borderColor: borderColor }}
                        onDoubleClick={resetTimer}>
                        <div>
                            <span className="task">{state.task}</span>
                            {state.taskAux &&
                                <>
                                    <br />
                                    <span className="text-secondary"> {state.taskAux}</span>
                                </>
                            }
                        </div>
                    </div>
                </div>

                {!state.isOptionsShown ?
                    <div className="row g-2 g-sm-3 my-2">
                        <div className="col-12 col-sm-6">
                            <Button variant="light"
                                size="lg"
                                className="col col-sm-6"
                                onClick={() => showOptions(true)}>
                                {!state.isTimerElapsed ? "Can't remember" : 'Show options'}
                            </Button>
                        </div>
                        <div className="col-12 col-sm-6">
                            <Button variant="light"
                                size="lg"
                                className="col col-sm-6"
                                disabled={state.isTimerElapsed}
                                onClick={() => showOptions(false)}>
                                {!state.isTimerElapsed ? `I have a guess (${state.interval})` : 'Time to make guess elapsed'}
                            </Button>
                        </div>
                    </div>
                    :
                    <>
                        <ChoiceOptions options={state.options}
                            backgrounds={state.backgrounds}
                            chooseOption={chooseOption} />

                        {!state.isTimerElapsed &&
                            <div className="row g-2 g-sm-3 my-2">
                                <div className="col-12 col-sm-6 mx-sm-auto">
                                    <Button variant="outline-secondary"
                                        size="lg"
                                        className={state.backgrounds[4] ?? ''}
                                        onClick={() => chooseOption(undefined, false)}>
                                        My guess was wrong
                                    </Button>
                                </div>
                            </div>
                        }

                        {state.isExampleShown || ((trainingState.isServicePanelEnabled || state.chosenOption?.content !== state.solution) && trainingState.isSolutionShown) ?
                            <div className="training-box mt-3">
                                <div className="example">
                                    <span style={{ whiteSpace: 'pre-wrap' }}>{state.example}</span>
                                </div>
                            </div>
                            :
                            <div className="row g-2 g-sm-3 my-3">
                                <div className="col-12 col-sm-6 mx-sm-auto my-0">
                                    <Button variant="outline-secondary"
                                        size="lg"
                                        className={state.backgrounds[4] ?? ''}
                                        onClick={() => showExample()}>
                                        Show example
                                    </Button>
                                </div>
                            </div>
                        }

                        {state.wrongOption &&
                            <div className="fs-5 mt-2"><span className="red">{state.wrongOption.content}</span> - <span className="fw-bold">{state.wrongOption.wrongOption}</span></div>
                        }

                        {(trainingState.isServicePanelEnabled || state.chosenOption?.content !== state.solution) && trainingState.isSolutionShown &&
                            <>
                                {trainingState.card?.similar &&
                                    <ul className="list-unstyled mt-2 mb-0 fs-5">
                                        {trainingState.card.similar.map(x =>
                                            <li key={x.id}><span className="fw-bold">{x.phrase}</span> - {x.meaning}</li>
                                        )}
                                    </ul>
                                }
                            </>
                        }
                    </>}
            </div>
        </>
    );
}
